import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../state/auth';
import { api } from '../../api/client';
import type { Workday } from '../../api/types';
import { ymdLocal } from '../../utils/time';

import { AdminDashboardPage } from './AdminDashboardPage';
import { AdminEmployeesPage } from './AdminEmployeesPage';
import { AdminWorkdaysPage } from './AdminWorkdaysPage';
import { AdminMessagesPage } from './AdminMessagesPage';
import { AdminLoginsPage } from './AdminLoginsPage';
import { AdminAuditPage } from './AdminAuditPage';
import { AdminCedolinoPage } from './AdminCedolinoPage';

function Icon({
  name
}: {
  name: 'dashboard' | 'employees' | 'workdays' | 'cedolino' | 'messages' | 'logins' | 'audit';
}) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg'
  } as const;

  const s = {
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  };

  if (name === 'dashboard') {
    return (
      <svg {...common}>
        <path d="M4 13h6V4H4v9zM14 20h6V11h-6v9zM14 4v5h6V4h-6zM4 20h6v-5H4v5z" {...s} />
      </svg>
    );
  }

  if (name === 'employees') {
    return (
      <svg {...common}>
        <path d="M16 11a4 4 0 1 0-8 0" {...s} />
        <path d="M12 14c-4.4 0-8 2.2-8 5v2h16v-2c0-2.8-3.6-5-8-5z" {...s} />
      </svg>
    );
  }

  if (name === 'workdays') {
    return (
      <svg {...common}>
        <path d="M7 3v3M17 3v3" {...s} />
        <path d="M4 8h16" {...s} />
        <path d="M6 6h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" {...s} />
        <path d="M8 12h4M8 16h8" {...s} />
      </svg>
    );
  }

  if (name === 'cedolino') {
    return (
      <svg {...common}>
        <path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-3 2-3-2-3 2-3-2V5a2 2 0 0 1 2-2z" {...s} />
        <path d="M8 8h8" {...s} />
        <path d="M8 12h8" {...s} />
        <path d="M8 16h5" {...s} />
      </svg>
    );
  }

  if (name === 'messages') {
    return (
      <svg {...common}>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z" {...s} />
      </svg>
    );
  }

  if (name === 'logins') {
    return (
      <svg {...common}>
        <path d="M10 17l5-5-5-5" {...s} />
        <path d="M15 12H3" {...s} />
        <path d="M21 3v18" {...s} />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 9v4" {...s} />
      <path d="M12 17h.01" {...s} />
      <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" {...s} />
    </svg>
  );
}

function NavItem({
  to,
  label,
  active,
  icon
}: {
  to: string;
  label: string;
  active: boolean;
  icon: React.ReactNode;
}) {
  return (
    <Link to={to} className={`sideNavItem ${active ? 'sideNavItemActive' : ''}`}>
      <span className="navIcon" aria-hidden="true">
        {icon}
      </span>
      <span style={{ fontWeight: 900 }}>
        {label}
      </span>
    </Link>
  );
}

function highlight(text: string, q: string) {
  if (!q.trim()) return text;

  const i = text.toLowerCase().indexOf(q.toLowerCase());

  if (i === -1) return text;

  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: 'rgba(255,136,0,.25)', padding: '0 2px', borderRadius: 4 }}>
        {text.slice(i, i + q.length)}
      </mark>
      {text.slice(i + q.length)}
    </>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const path = loc.pathname;

  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 1025);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1025);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [q, setQ] = useState('');
  const [results, setResults] = useState<Workday[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(async () => {
      if (!q.trim()) {
        setResults([]);
        setOpen(false);
        return;
      }

      try {
        const r = await api.get<{ items: Workday[] }>(
          `/api/admin/search?q=${encodeURIComponent(q.trim())}`
        );

        setResults(r.items);
        setOpen(true);
      } catch (e: any) {
        setResults([]);
        setOpen(false);
      }
    }, 250);

    return () => window.clearTimeout(t);
  }, [q]);

  if (isNarrow) {
    return (
      <div className="gateScreen">
        <div className="card gateCard">
          <div className="h2" style={{ marginTop: 0 }}>
            Gestionale Titolare
          </div>

          <div className="small">
            Questa dashboard è ottimizzata solo per desktop (sidebar laterale, tabelle, calendario).
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            Apri da un computer per gestire dipendenti e timbrature.
          </div>

          <div style={{ marginTop: 16 }} className="poweredBy">
            Powered by <b>Mattia Donizetti</b>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="adminApp">
      <aside className="adminSidebar">
        <div className="adminBrand">
          <div className="adminBrandTop">
            <div className="row" style={{ gap: 10 }}>
              <div>
                <img
                  className="h-15 sm:h-15 md:h-15"
                  src="/brand/infinityWifi.png"
                  alt="INFINITY WIFI"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="adminUserCard">
          <div className="adminUserName">
            {user?.first_name} {user?.last_name}
          </div>

          <div className="adminUserRole">
            Titolare
          </div>
        </div>

        <nav className="adminNav" aria-label="Navigazione">
          <NavItem
            to="/admin/dashboard"
            label="Dashboard"
            icon={<Icon name="dashboard" />}
            active={path.startsWith('/admin/dashboard') || path === '/admin'}
          />

          <NavItem
            to="/admin/employees"
            label="Dipendenti"
            icon={<Icon name="employees" />}
            active={path.startsWith('/admin/employees')}
          />

          <NavItem
            to="/admin/workdays"
            label="Timbrature"
            icon={<Icon name="workdays" />}
            active={path.startsWith('/admin/workdays')}
          />

          <NavItem
            to="/admin/cedolino"
            label="Cedolino"
            icon={<Icon name="cedolino" />}
            active={path.startsWith('/admin/cedolino')}
          />

          <NavItem
            to="/admin/messages"
            label="Messaggi"
            icon={<Icon name="messages" />}
            active={path.startsWith('/admin/messages')}
          />

          <NavItem
            to="/admin/logins"
            label="Login"
            icon={<Icon name="logins" />}
            active={path.startsWith('/admin/logins')}
          />

          <NavItem
            to="/admin/audit"
            label="Audit"
            icon={<Icon name="audit" />}
            active={path.startsWith('/admin/audit')}
          />
        </nav>

        <div className="adminSidebarFooter">
          <button className="btn btnSecondary" onClick={() => logout()}>
            Esci
          </button>

          <div className="poweredBy">
            Powered by <b>Mattia Donizetti</b>
          </div>
        </div>
      </aside>

      <div className="adminMain">
        <header className="adminTopbar">
          <div className="adminTopbarInner">
            <div style={{ display: 'grid', gap: 6, flex: 1, minWidth: 0 }}>
              <div className="small">
                Ricerca rapida (nome / data / località)
              </div>

              <input
                className="input"
                placeholder="Cerca per nome, data (YYYY-MM-DD o DD/MM/YYYY) o località…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <button
              className="btn btnGhost"
              onClick={() => {
                setQ('');
                setOpen(false);
              }}
            >
              Reset
            </button>
          </div>
        </header>

        <div className="adminMainContent">
          {open && results.length ? (
            <div className="container" style={{ marginTop: 14 }}>
              <div className="card" style={{ borderLeft: '6px solid var(--accent)' }}>
                <div className="spread">
                  <div className="h3" style={{ margin: 0 }}>
                    Risultati ricerca
                  </div>

                  <button className="btn btnGhost" onClick={() => setOpen(false)}>
                    Chiudi
                  </button>
                </div>

                <div className="small">
                  {results.length} risultati (max 200)
                </div>

                <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                  {results.slice(0, 20).map((w) => (
                    <Link
                      key={w._id}
                      to={`/admin/workdays?workdayId=${w._id}`}
                      className="card"
                      style={{
                        textDecoration: 'none',
                        boxShadow: 'none',
                        background: 'var(--surface-muted)'
                      }}
                    >
                      <div className="spread">
                        <div style={{ fontWeight: 900 }}>
                          {highlight(`${w.user?.first_name || '—'} ${w.user?.last_name || ''}`.trim(), q)}
                        </div>

                        <div className="badge badgeGray">
                          {ymdLocal(w.work_date)}
                        </div>
                      </div>

                      <div className="small" style={{ marginTop: 6 }}>
                        {highlight(
                          (
                            w.start_work_loc?.address ||
                            w.start_break_loc?.address ||
                            w.end_work_loc?.address ||
                            '—'
                          ),
                          q
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {results.length > 20 ? (
                  <div className="small" style={{ marginTop: 10 }}>
                    Mostrati i primi 20.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <main className="container" style={{ paddingBottom: 24, marginTop: 14 }}>
            <Routes>
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/dashboard" element={<AdminDashboardPage />} />
              <Route path="/employees" element={<AdminEmployeesPage />} />
              <Route path="/workdays" element={<AdminWorkdaysPage />} />
              <Route path="/cedolino" element={<AdminCedolinoPage />} />
              <Route path="/messages" element={<AdminMessagesPage />} />
              <Route path="/logins" element={<AdminLoginsPage />} />
              <Route path="/audit" element={<AdminAuditPage />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}